import { useCallback, useEffect, useState } from "react";

/**
 * The one transient notice for "this forecast is no longer shared with you", also used by the
 * `?forecast=` deep link to say a branch link opened its root, or a link could not open (SOO-862).
 *
 * A hook rather than two more `useState`s in AppInner — the state-architecture
 * convention (and the ratchet's `appInnerUseState` counter) wants new stateful
 * behaviour to live in its own slice.
 *
 * It holds only the message. The DECISION that a refusal means access loss, and
 * the removal of the row, belong to `lib/forecast/sharedAccessLoss.js`; by the
 * time a message arrives here the row is already gone from the list. Keeping the
 * two apart is what lets the rule be unit-tested without React.
 *
 * The notice auto-clears, because it reports something that ALREADY happened and
 * needs no decision from the user — a dismissal they must perform to keep working
 * would be a worse version of the same sentence.
 */
export function useSharedAccessNotice({ timeoutMs = 6000 } = {}) {
  const [accessNotice, setAccessNotice] = useState(null);

  useEffect(() => {
    if (!accessNotice) return;
    const t = setTimeout(() => setAccessNotice(null), timeoutMs);
    return () => clearTimeout(t);
  }, [accessNotice, timeoutMs]);

  // Identity-stable so it can sit in the dep arrays of the write hooks that call
  // it (useTrackingPrefs' applySchedule/toggleWatchlist) without re-creating them
  // on every render.
  const noticeShown = useCallback((message) => setAccessNotice(message || null), []);
  const noticeCleared = useCallback(() => setAccessNotice(null), []);

  return { accessNotice, noticeShown, noticeCleared };
}
